import { debounce } from 'lodash';
import { initNumberField } from '../../scripts/helpers';
import { changeCart, removeMultipleProducts } from '../../scripts/services/cart';
import { closeModal, openModal } from './modals';

/**
 * Finds the minicart element within the specified root element.
 *
 * @param {Document|HTMLElement} [root=document] - The root element to search within.
 * @returns {HTMLElement|null} The minicart element if found, otherwise null.
 */
const findMinicart = (root = document) => root.querySelector('#MinicartDrawer');

/**
 * Initializes the minicart by setting up event listeners and initializing line items.
 */
const initMinicart = () => {
  const minicart = findMinicart();
  if (!minicart) return;

  const privacyCheckbox = minicart.querySelector(
    '.privacy-checkbox__container',
  );
  if (
    privacyCheckbox &&
    typeof window.dAgency.initPrivacyCheckbox == 'function'
  )
    window.dAgency.initPrivacyCheckbox(privacyCheckbox);

  minicart.querySelectorAll('.cart-line__wrapper[data-key]').forEach((line) =>
    initLine({
      line,
      beforeFetch: () => {
        minicart.classList.add('loading');
      },
      afterFetch: () => {
        updateMinicart();
      },
    }),
  );
  groupCartItems(minicart);
};

/**
 * Raggruppa gli articoli nel minicart
 * @param {HTMLElement} minicart - Il minicart da aggiornare
 */
const groupCartItems = (minicart) => {
  const lines = Array.from(minicart.querySelectorAll('.cart-line__wrapper'));
  const modalBodyInner = minicart.querySelector('.modal__body-inner');

  if (modalBodyInner) {
    modalBodyInner.innerHTML = '';
  }

  const groups = lines.reduce((acc, line) => {
    const groupId = line.dataset.group || 'default';
    if (!acc[groupId]) acc[groupId] = [];
    acc[groupId].push(line);
    return acc;
  }, {});

  Object.entries(groups).forEach(([groupId, groupLines]) => {
    let groupWrapper = modalBodyInner.querySelector(
      `.cart-line-group[data-group-id="${groupId}"]`,
    );
    if (!groupWrapper) {
      groupWrapper = document.createElement('div');
      groupWrapper.classList.add('cart-line-group');
      groupWrapper.dataset.groupId = groupId;
      modalBodyInner.appendChild(groupWrapper);
    }

    groupLines.forEach((line) => {
      groupWrapper.appendChild(line);
    });
  });
};

const updateMinicart = async () => {
  const minicart = findMinicart();
  if (!minicart) return;
  minicart.classList.add('loading');
  await fetch(minicart.dataset.refreshUrl)
    .then((res) => res.text())
    .then((data) => {
      const minicartSection = new DOMParser().parseFromString(
        data,
        'text/html',
      );
      const fetchMinicart = findMinicart(minicartSection);
      if (fetchMinicart) minicart.innerHTML = fetchMinicart.innerHTML;
      minicart
        .querySelectorAll('.modal__backdrop, .modal__close')
        .forEach((closer) =>
          closer.addEventListener('click', () => closeModal(minicart)),
        );

      initMinicart();
    });
  minicart.classList.remove('loading');
};

/**
 * Initializes a line item in the minicart by setting up event listeners for
 * quantity changes and item removal.
 * @param {Object} options - Options for initializing the line item.
 * @param {HTMLElement} options.line - The line item element.
 * @param {Function} [options.beforeFetch] - Function to call before sending the request.
 * @param {Function} [options.afterFetch] - Function to call after successfully changing the cart item.
 */
const initLine = ({ line, beforeFetch, afterFetch }) => {
  if (!line) return;
  const minusBtn = line.querySelector('.cart-line__quantity-btn--minus');
  const plusBtn = line.querySelector('.cart-line__quantity-btn--plus');
  const field = line.querySelector('.cart-line__quantity-value');

  if (minusBtn && plusBtn && field) {
    initNumberField({
      plusBtn,
      minusBtn,
      field,
    });

    field.addEventListener(
      'change',
      debounce(() => {
        changeCart({
          key: line.dataset.key,
          quantity: parseInt(field.value),
          callbacks: {
            beforeFetch: beforeFetch,
            afterFetch: afterFetch,
          },
        });
      }, 300),
    );
  }

  const remove = line.querySelector('.cart-line__remove');
  const animationDuration = 900;
  if (remove) {
    remove.addEventListener('click', async (e) => {

      e.preventDefault();
      let groupId = e.target.closest('.cart-line-group').dataset.groupId;
      let groupEl = e.target.closest(`.cart-line-group[data-group-id="${groupId}"]`);
      let children = e.target.closest('.cart-line-group').children;
      const childrenKey = [];

      // cart page
      if (e.target.classList.contains('cart-page') && e.target.classList.contains('data-master') && children.length > 1) {

        Array.from(children).forEach((child) => {
          childrenKey.push(child.dataset.key);
        });
        const toRemove = groupEl;
        toRemove.style.height = `${toRemove.clientHeight}px`;
        toRemove.style.overflow = 'hidden';
        toRemove.style.transition = `height ${animationDuration}ms`;
        window.requestAnimationFrame(() => {
          toRemove.style.height = '0px';
          setTimeout(async () => {
            toRemove.remove();
            try {
              const cart = await removeMultipleProducts(childrenKey);
              if (cart.item_count <= 0) {
                document.querySelector('.cart-page__empty').classList.remove('hidden');
              } else {
                document.querySelector('.cart-page__empty').classList.add('hidden');
              }
            } catch (err) {
              console.error("Errore durante la rimozione:", err);
            }
          }, animationDuration);
        });

      } else {
        if (groupEl && line.hasAttribute("data-master-product")) {
          const engravingEl = groupEl.querySelector('[data-engraving]');
          if (engravingEl) {
            const engravingElKey = engravingEl.dataset.key;
            if (engravingElKey) {
              const itemsToRemove = [line.dataset.key, engravingElKey];
              await removeMultipleProducts(itemsToRemove);
            }
          } else {
            changeCart({
              key: line.dataset.key,
              quantity: 0,
              callbacks: {
                beforeFetch: beforeFetch,
                afterFetch: afterFetch,
              },
            });
          }
        } else {
          changeCart({
            key: line.dataset.key,
            quantity: 0,
            callbacks: {
              beforeFetch: beforeFetch,
              afterFetch: afterFetch,
            },
          });
        }
        }

    });
  }
};

/**
 * Opens the minicart, optionally loading its content before opening.
 *
 * @param {boolean} [loadBeforeOpen=false] - Whether to load the minicart content before opening it.
 */
const openMinicart = async (loadBeforeOpen = false) => {
  if (loadBeforeOpen) await updateMinicart();
  openModal(findMinicart());
};

/**
 * Closes the minicart.
 */
const closeMinicart = () => closeModal(findMinicart());

export {
  initMinicart,
  openMinicart,
  closeMinicart,
  initLine,
  updateMinicart
};
